import { useState, type Dispatch, type SetStateAction } from 'react';

export type TableDensity = 'cozy' | 'compact';

export interface UseColumnVisibilityResult<TColumns extends Record<string, boolean>> {
  density: TableDensity;
  setDensity: Dispatch<SetStateAction<TableDensity>>;
  visibleColumns: TColumns;
  setVisibleColumns: Dispatch<SetStateAction<TColumns>>;
  toggleColumn: (column: keyof TColumns) => void;
}

// Table density and per-column visibility are display preferences, independent of which
// records are being shown — they intentionally do NOT reset when list filters change (unlike
// `page`, which correctly resets to show page 1 of the new result set). Keep this state on its
// own hook instance per page so a future filter-reset refactor can't accidentally sweep it in.
//
// `defaultColumns` is typed as `Record<K, boolean>` (rather than inferring the generic directly
// from the argument) so each column's `true`/`false` literal widens to `boolean` — otherwise
// callers couldn't toggle a column whose default happened to be `true` or `false`.
export function useColumnVisibility<K extends string>(
  defaultColumns: Record<K, boolean>,
): UseColumnVisibilityResult<Record<K, boolean>> {
  const [density, setDensity] = useState<TableDensity>('cozy');
  const [visibleColumns, setVisibleColumns] = useState<Record<K, boolean>>(defaultColumns);

  function toggleColumn(column: K) {
    setVisibleColumns((current) => ({ ...current, [column]: !current[column] }));
  }

  return { density, setDensity, visibleColumns, setVisibleColumns, toggleColumn };
}
